import {useMemo} from 'react'
import {ownerAvatarPath} from '@github-ui/paths'
import type {AgentInfo} from '@github-ui/agent-sessions-types/agent-identity'
import type {SubAgent} from '@github-ui/agent-sessions-types/sub-agent'
import type {FilterOption} from '../utils/filter-utils'
import {toDisplaySlug} from '../utils/filter-utils'
import {isCopilotCodingAgent} from '@github-ui/copilot-shared-ui/agents/agent-helpers'
import {getFilterIconForSlug} from '../agents/agent-profile'
import {getAgentDisplayName} from '@github-ui/copilot-shared-ui/agents/agent-info-registry'

interface ApiCreator {
  id: number
  login?: string
}

/**
 * Builds creator filter options from the /tasks/creators API endpoint.
 * Current user is pinned first; remaining creators use API order.
 */
export function useCreatorOptions(apiCreators?: ApiCreator[], currentUserLogin?: string): FilterOption[] {
  return useMemo(() => {
    const map = new Map<string, FilterOption>()

    // Pin current user at the top when available
    if (currentUserLogin) {
      map.set(currentUserLogin, {
        value: currentUserLogin,
        label: currentUserLogin,
        avatarUrl: ownerAvatarPath({owner: currentUserLogin}),
      })
    }

    // API-sourced creators (authoritative, covers full repo)
    if (apiCreators) {
      for (const creator of apiCreators) {
        const login = creator.login
        if (login && !map.has(login)) {
          map.set(login, {
            value: login,
            label: login,
            avatarUrl: ownerAvatarPath({owner: login}),
          })
        }
      }
    }

    return Array.from(map.values())
  }, [apiCreators, currentUserLogin])
}

/**
 * Builds agent filter options from two sources:
 * 1. Top-level agents API (monolith) — authoritative list of built-in agents.
 *    Uses display-name-derived slugs as option values (e.g. "copilot-coding-agent",
 *    "copilot-cli") for human-readable URLs and search bar qualifiers.
 * 2. Custom agents API (CAPI via useSubAgents) — repo-specific agents defined
 *    by `.md` files. These use "custom:<name>" as the value.
 *
 * Agents with group "review" or "automation" are excluded — review agents
 * are shown in the Code Reviews tab, and "GitHub Advanced Security" (code
 * scanning, group "automation") is hidden from the agent filter.
 *
 * Exported for testing; prefer the hook version in components.
 */
export function buildAgentOptions(apiAgents?: AgentInfo[], customAgents?: SubAgent[]): FilterOption[] {
  if (!apiAgents) return []

  const copilotOptions: FilterOption[] = []
  const topLevelOptions: FilterOption[] = []
  const customOptions: FilterOption[] = []
  const seenDisplaySlugs = new Set<string>()

  // Built-in agents from the monolith API — use display-name slugs as values.
  // Skip review-group agents since they have a dedicated Code Reviews tab.
  for (const agent of apiAgents) {
    if (agent.group === 'review' || agent.group === 'automation') continue
    const displayName = getAgentDisplayName(agent)
    const displaySlug = toDisplaySlug(displayName)
    if (seenDisplaySlugs.has(displaySlug)) continue
    seenDisplaySlugs.add(displaySlug)

    const option: FilterOption = {
      value: displaySlug,
      label: displayName || 'Copilot',
      icon: getFilterIconForSlug(agent.slug),
      avatarUrl: agent.avatar_url || undefined,
      group: 'agents',
      agentType: agent.group,
    }

    if (isCopilotCodingAgent(agent.slug)) {
      copilotOptions.push(option)
    } else {
      topLevelOptions.push(option)
    }
  }

  // Custom agents from the CAPI endpoint (repo-specific .md agents)
  if (customAgents) {
    for (const agent of customAgents) {
      const key = `custom:${agent.name}`
      customOptions.push({
        value: key,
        label: agent.display_name || agent.name,
        group: 'custom',
      })
    }
  }

  // Sort non-Copilot groups alphabetically
  topLevelOptions.sort((a, b) => a.label.localeCompare(b.label))
  customOptions.sort((a, b) => a.label.localeCompare(b.label))

  // Order: Copilot first, then other top-level agents, then custom agents
  return [...copilotOptions, ...topLevelOptions, ...customOptions]
}

export function useAgentOptions(apiAgents?: AgentInfo[], customAgents?: SubAgent[]): FilterOption[] {
  return useMemo(() => buildAgentOptions(apiAgents, customAgents), [apiAgents, customAgents])
}
